"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import PageHeaderWithStats from "@/components/common/PageHeaderWithStats";
import {
  PlusIcon,
  ChatBubbleLeftIcon,
  HeartIcon,
  FireIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  SparklesIcon,
  ChartBarIcon,
  StarIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChatBubbleBottomCenterTextIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";

import { toast } from "react-hot-toast";
import { AuthStorage } from "@/lib/storage";
import { User } from "@/types";
import { Footer } from "@/components/Footer";
import { COMMUNITY_CATEGORIES } from "@/lib/community-categories";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import InlineLoader from "@/components/common/InlineLoader";

// Local CommunityPost interface that includes all properties
interface CommunityPost {
  id: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  title: string;
  content: string;
  category: string;
  tags: string[];
  likes: string[];
  comments: Comment[];
  views?: number;
  isHot?: boolean;
  isPinned?: boolean;
  isNew?: boolean;
  images?: string[];
  createdAt: string;
  updatedAt: string;
}

interface Comment {
  id: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  content: string;
  likes: string[];
  replies?: Comment[];
  createdAt: string;
  updatedAt: string;
}

type SortBy = "recent" | "popular" | "trending" | "hot";

interface CommunityStats {
  totalPosts: number;
  activeMembers: number;
  totalUsers: number;
  categoriesCount: number;
  totalLikes: number;
  totalComments: number;
  lastUpdated: string;
}

import { useAuthSession } from "@/hooks/useAuthSession";

// ... existing imports

const CommunityPage: React.FC = () => {
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortBy>("recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { user, isLoading } = useAuthSession();

  const [stats] = useState<CommunityStats | null>(null);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  const categories = COMMUNITY_CATEGORIES;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch posts
  const fetchPosts = useCallback(
    async (page = 1, reset = false) => {
      try {
        setLoading(true);

        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: "10",
          sortBy,
        });

        if (selectedCategory) queryParams.append("category", selectedCategory);
        if (debouncedSearchQuery.trim())
          queryParams.append("search", debouncedSearchQuery.trim());

        const response = await fetch(
          `/api/community?${queryParams.toString()}`
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData.error || `Failed to fetch posts (${response.status})`;
          throw new Error(errorMessage);
        }

        const data = await response.json();

        if (reset) {
          setPosts(data.posts || []);
        } else {
          setPosts((prev) => [...prev, ...(data.posts || [])]);
        }

        // Safe access to pagination with fallbacks
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages || 1);
        } else {
          setTotalPages(1);
        }
        setCurrentPage(page);
      } catch (error) {
        console.error("Error fetching posts:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to load community posts. Please try again."
        );

        // If this is the first page and there's an error, set empty array
        if (reset) {
          setPosts([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearchQuery, selectedCategory, sortBy]
  );

  // Initial load and filter changes
  useEffect(() => {
    fetchPosts(1, true);
  }, [fetchPosts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        isCategoryDropdownOpen &&
        !target.closest("[data-dropdown-container]")
      ) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isCategoryDropdownOpen]);

  // Helper function to strip HTML tags from content (XSS-safe)
  const stripHtmlTags = (html: string) => {
    return html.replace(/<[^>]*>/g, "");
  };

  // Helper function to get clean content preview
  const getContentPreview = (content: string, maxLength = 200) => {
    const cleanContent = stripHtmlTags(content);
    return cleanContent.length > maxLength
      ? `${cleanContent.substring(0, maxLength)}...`
      : cleanContent;
  };

  // Format numbers for display (e.g., 1200 -> 1.2K)
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    } else {
      return num.toString();
    }
  };

  const formatTimestamp = (timestamp: string | Date) => {
    const date =
      typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return "Yesterday";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Client-side sorting function to ensure proper filtering
  const sortPosts = useCallback((posts: CommunityPost[], sortBy: SortBy) => {
    const sortedPosts = [...posts];

    switch (sortBy) {
      case "recent":
        return sortedPosts.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      case "popular":
        return sortedPosts.sort((a, b) => b.likes.length - a.likes.length);
      case "trending":
        // Trending based on recent activity (likes + comments + views)
        return sortedPosts.sort((a, b) => {
          const aScore =
            a.likes.length + (a.comments?.length || 0) + (a.views || 0);
          const bScore =
            b.likes.length + (b.comments?.length || 0) + (b.views || 0);
          return bScore - aScore;
        });
      case "hot":
        // Hot posts are explicitly marked or have high engagement
        return sortedPosts.sort((a, b) => {
          if (a.isHot && !b.isHot) return -1;
          if (!a.isHot && b.isHot) return 1;
          // If both or neither are hot, sort by engagement
          const aScore = a.likes.length + (a.comments?.length || 0);
          const bScore = b.likes.length + (b.comments?.length || 0);
          return bScore - aScore;
        });
      default:
        return sortedPosts;
    }
  }, []);

  // Get filtered and sorted posts
  const displayPosts = useCallback(() => {
    let filtered = posts;

    // Apply search filter if there's a search query
    if (searchQuery.trim()) {
      filtered = posts.filter(
        (post) =>
          post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          stripHtmlTags(post.content)
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          post.author.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.tags.some((tag) =>
            tag.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter((post) => post.category === selectedCategory);
    }

    // Apply sorting
    return sortPosts(filtered, sortBy);
  }, [posts, searchQuery, selectedCategory, sortBy, sortPosts]);

  const filteredAndSortedPosts = displayPosts();

  // Function to count posts by category
  const getPostCountByCategory = useCallback(
    (category: string) => {
      return posts.filter((post) => post.category === category).length;
    },
    [posts]
  );

  // Get total post count
  const totalPostCount = posts.length;

  console.log("post", posts);
  // Handle like post
  const handleLikePost = async (postId: string) => {
    if (!user) {
      toast.error("Please login to like posts");
      return;
    }

    console.log("Attempting to like post with ID:", postId);
    console.log("User ID:", user.id);

    try {
      const response = await fetch(`/api/community/${postId}/like`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Like API error:", response.status, errorData);
        throw new Error(`Failed to like post: ${response.status}`);
      }

      const data = await response.json();

      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? {
              ...post,
              likes: data.liked
                ? ([...post.likes, user?.id].filter(Boolean) as string[])
                : post.likes.filter((id) => id !== user?.id),
            }
            : post
        )
      );
    } catch (error) {
      console.error("Error liking post:", error);
      toast.error("Failed to like post");
    }
  };

  console.log("Community - User:", user);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px] mb-[40px]">
      {/* Header and Stats Section */}
      <PageHeaderWithStats
        badge={{ text: "Community Forum", showPulse: true }}
        title={{ main: "Connect & Share", highlighted: "Together" }}
        description="Join our vibrant yoga and wellness community. Connect, share, and grow together."
        stats={[
          {
            icon: ChatBubbleLeftIcon,
            value: stats?.totalPosts ? stats.totalPosts : posts.length,
            label: "Total Posts",
            formatter: (val) => formatNumber(typeof val === "number" ? val : 0),
          },
          {
            icon: TagIcon,
            value: stats?.categoriesCount || categories.length,
            label: "Categories",
          },
          {
            icon: HeartIcon,
            value:
              stats?.totalLikes ||
              posts.reduce((sum, post) => sum + (post.likes?.length || 0), 0),
            label: "Total Likes",
            formatter: (val) => formatNumber(typeof val === "number" ? val : 0),
          },
          {
            icon: ChatBubbleBottomCenterTextIcon,
            value: stats?.totalComments || 0,
            label: "Total Replies",
            formatter: (val) => formatNumber(typeof val === "number" ? val : 0),
            isVisible: stats?.totalComments ? true : false,
          },
        ]}
        loading={isLoading}
      />

      {isLoading ? (
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 lg:py-12 zoom-container">
          <LoadingSpinner
            fullScreen={false}
            showText={false}
            className="py-8 lg:py-12"
          />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 lg:px-6 zoom-container">
          {/* Modern Search and Action Section */}
          <div className="mb-4 lg:mb-6">
            <div className="flex flex-col gap-4 items-stretch lg:flex-row lg:items-center lg:justify-between">
              {/* Search Bar */}
              <div className="relative flex-1 w-full">
                <input
                  type="text"
                  placeholder="Search discussions, topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-12 pr-12 lg:px-6 lg:py-4 lg:pl-14 lg:pr-16 rounded-xl lg:rounded-2xl border-2 border-gray-200 bg-white/90 backdrop-blur-sm text-gray-700 placeholder-gray-500 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 shadow-lg hover:shadow-xl text-sm lg:text-base"
                />
                <MagnifyingGlassIcon className="w-5 h-5 lg:w-6 lg:h-6 text-gray-400 absolute left-3 lg:left-5 top-1/2 transform -translate-y-1/2" />
                <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-orange-500 hover:bg-orange-600 text-white p-1.5 lg:p-2 rounded-lg lg:rounded-xl transition-colors duration-300 group">
                  <ArrowRightIcon className="w-4 h-4 lg:w-5 lg:h-5 group-hover:scale-110 transition-transform" />
                </button>
              </div>

              {/* Create Post Button */}
              <div className="flex-shrink-0 w-full lg:w-auto">
                {user ? (
                  <Link
                    href="/community/create"
                    className="inline-flex items-center justify-center gap-2 w-full lg:w-auto px-4 py-3 lg:px-6 lg:py-4 bg-orange-500 text-white rounded-xl lg:rounded-2xl hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm lg:text-base font-medium"
                  >
                    <PlusIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                    Create New Post
                  </Link>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Link
                      href="/login?redirect=/community/create"
                      className="inline-flex items-center justify-center gap-2 w-full lg:w-auto px-4 py-3 lg:px-6 lg:py-4 bg-orange-500 text-white rounded-xl lg:rounded-2xl hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm lg:text-base font-medium"
                    >
                      <PlusIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                      Create New Post
                    </Link>
                    <p className="text-xs text-gray-500 text-center lg:text-right">
                      Login required to create posts
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter Info */}
          <div className="flex justify-center mb-4 lg:mb-6">
            {(searchQuery || selectedCategory) && (
              <div className="flex items-center gap-2">
                <span className="text-xs lg:text-sm px-3 py-1.5 lg:px-3 lg:py-1 bg-orange-500/10 text-orange-600 rounded-full font-medium">
                  {filteredAndSortedPosts.length} discussions found
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-col lg:grid lg:grid-cols-4 gap-6 lg:gap-8">
            {/* Sidebar - Mobile: Full width, Desktop: 1/4 width */}
            <div className="lg:col-span-1 space-y-4 lg:space-y-6">
              {/* Categories */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 lg:p-6 shadow-lg border border-gray-200">
                {/* Mobile Category Dropdown */}
                <div className="lg:hidden" data-dropdown-container>
                  <button
                    onClick={() =>
                      setIsCategoryDropdownOpen(!isCategoryDropdownOpen)
                    }
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FunnelIcon className="w-4 h-4 text-orange-500" />
                      <span className="font-medium text-gray-900 truncate">
                        {selectedCategory || "All Categories"}
                      </span>
                      {selectedCategory && (
                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                          {getPostCountByCategory(selectedCategory)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedCategory && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCategory("");
                            setIsCategoryDropdownOpen(false);
                          }}
                          className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                          title="Clear filter"
                        >
                          <svg
                            className="w-3 h-3 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                      {isCategoryDropdownOpen ? (
                        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {isCategoryDropdownOpen && (
                    <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 bg-white rounded-xl shadow-lg z-10 relative">
                      <button
                        onClick={() => {
                          setSelectedCategory("");
                          setIsCategoryDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-b-0 ${!selectedCategory
                            ? "bg-orange-50 text-orange-600 font-medium"
                            : "hover:bg-gray-50 text-gray-700 active:bg-gray-100"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>All Categories</span>
                          <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full font-medium">
                            {totalPostCount}
                          </span>
                        </div>
                      </button>
                      {categories.map((category) => {
                        const postCount = getPostCountByCategory(category);
                        return (
                          <button
                            key={category}
                            onClick={() => {
                              setSelectedCategory(category);
                              setIsCategoryDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-b-0 ${selectedCategory === category
                                ? "bg-orange-50 text-orange-600 font-medium"
                                : "hover:bg-gray-50 text-gray-700 active:bg-gray-100"
                              }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm pr-2 leading-tight">
                                {category}
                              </span>
                              <span
                                className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${selectedCategory === category
                                    ? "bg-orange-200 text-orange-700"
                                    : postCount > 0
                                      ? "bg-orange-100 text-orange-600"
                                      : "bg-gray-100 text-gray-500"
                                  }`}
                              >
                                {postCount}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Desktop Category List */}
                <div className="hidden lg:block">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FunnelIcon className="w-5 h-5 text-orange-500" />
                    Categories
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedCategory("")}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center justify-between group ${!selectedCategory
                          ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                          : "hover:bg-gray-50 text-gray-700 hover:shadow-md"
                        }`}
                    >
                      <span className="font-medium">All Categories</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${!selectedCategory
                            ? "bg-white/20 text-white"
                            : "bg-orange-100 text-orange-600 group-hover:bg-orange-200"
                          }`}
                      >
                        {totalPostCount}
                      </span>
                    </button>
                    {categories.map((category) => {
                      const postCount = getPostCountByCategory(category);
                      return (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 text-sm flex items-center justify-between group ${selectedCategory === category
                              ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg"
                              : "hover:bg-gray-50 text-gray-700 hover:shadow-md"
                            }`}
                        >
                          <span
                            className={`${selectedCategory === category ? "font-medium" : "font-normal"}`}
                          >
                            {category}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${selectedCategory === category
                                ? "bg-white/20 text-white"
                                : postCount > 0
                                  ? "bg-orange-100 text-orange-600 group-hover:bg-orange-200"
                                  : "bg-gray-100 text-gray-500 group-hover:bg-gray-200"
                              }`}
                          >
                            {postCount}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content - Mobile: Full width, Desktop: 3/4 width */}
            <div className="lg:col-span-3 space-y-4 lg:space-y-6">
              {/* Sort Options */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 lg:p-6 shadow-lg border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:flex-wrap justify-between items-start sm:items-center mb-3 lg:mb-4 gap-3">
                  <h3 className="text-base lg:text-lg font-semibold text-gray-900">
                    Sort Posts
                  </h3>
                  {(searchQuery || selectedCategory) && (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedCategory("");
                        setSortBy("recent");
                      }}
                      className="text-xs px-3 py-1 bg-orange-500/10 text-orange-600 rounded-full hover:bg-orange-500/20 transition-colors"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 lg:flex lg:flex-wrap lg:gap-2">
                  {[
                    { key: "recent", label: "Recent", icon: ClockIcon },
                    { key: "popular", label: "Popular", icon: HeartIcon },
                    { key: "trending", label: "Trending", icon: ChartBarIcon },
                  ].map((option) => (
                    <button
                      key={option.key}
                      onClick={() =>
                        setSortBy(
                          option.key as
                          | "recent"
                          | "popular"
                          | "trending"
                          | "hot"
                        )
                      }
                      className={`flex flex-col lg:flex-row items-center justify-center lg:gap-2 px-2 lg:px-4 py-2 lg:py-2 rounded-lg transition-all text-xs lg:text-sm ${sortBy === option.key
                          ? "bg-orange-500 text-white shadow-md"
                          : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                    >
                      <option.icon className="w-3 h-3 lg:w-4 lg:h-4 mb-1 lg:mb-0" />
                      <span className="leading-tight">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Posts List */}
              {loading && posts.length === 0 ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, index) => (
                    <div
                      key={index}
                      className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-200 animate-pulse"
                    >
                      <div className="flex gap-4">
                        <div className="w-12 h-12 bg-gray-200 rounded-full" />
                        <div className="flex-1 space-y-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4" />
                          <div className="h-3 bg-gray-200 rounded w-1/2" />
                          <div className="flex gap-4">
                            <div className="h-3 bg-gray-200 rounded w-16" />
                            <div className="h-3 bg-gray-200 rounded w-16" />
                            <div className="h-3 bg-gray-200 rounded w-16" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4 lg:space-y-6">
                  {filteredAndSortedPosts.map((post, index) => (
                    <div
                      key={post.id}
                      className="bg-white/95 backdrop-blur-sm rounded-xl p-4 lg:p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 group animate-slideUp"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex gap-3 lg:gap-4">
                        {/* Author Avatar */}
                        <div className="flex-shrink-0">
                          <div className="relative w-10 h-10 lg:w-12 lg:h-12">
                            {post.author.avatar &&
                              post.author.avatar.trim() !== "" ? (
                              <Image
                                src={post.author.avatar}
                                alt={post.author.name}
                                fill
                                className="rounded-full object-cover ring-2 ring-orange-500/20"
                              />
                            ) : (
                              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-semibold text-sm lg:text-lg ring-2 ring-orange-500/20">
                                {post.author.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Post Content */}
                        <div className="flex-1 space-y-2 lg:space-y-3 min-w-0">
                          {/* Header */}
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1 min-w-0">
                              <div className="flex items-start flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                <Link
                                  href={`/community/${post.id}`}
                                  className="text-base lg:text-lg font-semibold text-gray-900 group-hover:text-orange-500 transition-colors line-clamp-2 pr-2"
                                >
                                  {post.title}
                                </Link>
                                <div className="flex items-center gap-1 flex-wrap">
                                  {post.isPinned && (
                                    <span className="px-2 py-0.5 bg-yellow-500/10 text-yellow-600 text-xs rounded-full flex items-center gap-1">
                                      <StarIcon className="w-3 h-3" />
                                      <span className="hidden sm:inline">
                                        Pinned
                                      </span>
                                    </span>
                                  )}
                                  {post.isHot && (
                                    <span className="px-2 py-0.5 bg-red-500/10 text-red-500 text-xs rounded-full flex items-center gap-1">
                                      <FireIcon className="w-3 h-3" />
                                      <span className="hidden sm:inline">
                                        Hot
                                      </span>
                                    </span>
                                  )}
                                  {post.isNew && (
                                    <span className="px-2 py-0.5 bg-green-500/10 text-green-500 text-xs rounded-full flex items-center gap-1">
                                      <SparklesIcon className="w-3 h-3" />
                                      <span className="hidden sm:inline">
                                        New
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 lg:gap-4 text-xs lg:text-sm text-gray-600 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <UserIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                                  <span className="truncate max-w-20 lg:max-w-none">
                                    {post.author.name}
                                  </span>
                                </span>
                                <span className="flex items-center gap-1">
                                  <ClockIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                                  <span className="hidden sm:inline">
                                    {formatTimestamp(post.createdAt)}
                                  </span>
                                  <span className="sm:hidden">
                                    {
                                      formatTimestamp(post.createdAt).split(
                                        " "
                                      )[0]
                                    }
                                  </span>
                                </span>
                                <span className="flex items-center gap-1 hidden sm:flex">
                                  <TagIcon className="w-3 h-3 lg:w-4 lg:h-4" />
                                  <span className="truncate">
                                    {post.category}
                                  </span>
                                </span>
                                <span className="flex items-center gap-1 hidden lg:flex">
                                  <EyeIcon className="w-4 h-4" />
                                  {post.views} views
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Content Preview */}
                          <div className="text-gray-700 line-clamp-2 lg:line-clamp-3 text-sm lg:text-base">
                            {getContentPreview(post.content)}
                          </div>

                          {/* Tags - Show category on mobile, tags on desktop */}
                          <div className="flex flex-wrap gap-1 lg:gap-2">
                            <span className="sm:hidden px-2 py-1 bg-orange-50 text-orange-600 text-xs rounded-full">
                              {post.category}
                            </span>
                            {post.tags && post.tags.length > 0 && (
                              <>
                                {post.tags.slice(0, 2).map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-2 lg:px-3 py-1 bg-gray-50 text-gray-600 text-xs rounded-full hover:bg-orange-50 hover:text-orange-600 transition-colors cursor-pointer"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                                {post.tags.length > 2 && (
                                  <span className="text-xs text-gray-500 px-2 py-1">
                                    +{post.tags.length - 2} more
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          {/* Images - Show all images from the post */}
                          {post.images && post.images.length > 0 && (
                            <div className="space-y-3">
                              {/* Show first image with larger size */}
                              <div className="relative w-full h-48 rounded-xl overflow-hidden">
                                <Image
                                  src={post.images[0]}
                                  alt="Post image"
                                  fill
                                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              </div>

                              {/* Show indicator for multiple images */}
                              {post.images.length > 1 && (
                                <div className="flex items-center justify-center">
                                  <span className="text-sm text-gray-600 bg-gray-50 px-3 py-1 rounded-full border border-gray-200">
                                    📸 {post.images.length} image
                                    {post.images.length > 1 ? "s" : ""}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center justify-between pt-2 lg:pt-3 border-t border-gray-200">
                            <div className="flex items-center gap-4 lg:gap-6">
                              {user ? (
                                <button
                                  onClick={() => handleLikePost(post.id!)}
                                  className="flex items-center gap-1 lg:gap-2 text-gray-600 hover:text-orange-500 transition-colors"
                                >
                                  {post.likes.includes(user?.id || "") ? (
                                    <HeartSolidIcon className="w-4 h-4 lg:w-5 lg:h-5 text-orange-500" />
                                  ) : (
                                    <HeartIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                  )}
                                  <span className="text-xs lg:text-sm font-medium">
                                    {post.likes.length}
                                  </span>
                                </button>
                              ) : (
                                <div className="group relative">
                                  <button
                                    onClick={() =>
                                      toast.error("Please login to like posts")
                                    }
                                    className="flex items-center gap-1 lg:gap-2 text-gray-600 hover:text-orange-500 transition-colors"
                                  >
                                    <HeartIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                    <span className="text-xs lg:text-sm font-medium">
                                      {post.likes.length}
                                    </span>
                                  </button>
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap hidden lg:block">
                                    Login to like posts
                                  </div>
                                </div>
                              )}
                              <Link
                                href={`/community/${post.id}`}
                                className="flex items-center gap-1 lg:gap-2 text-gray-600 hover:text-orange-500 transition-colors"
                              >
                                <ChatBubbleLeftIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                                <span className="text-xs lg:text-sm font-medium">
                                  {post.comments?.length || 0}
                                </span>
                              </Link>
                            </div>
                            <Link
                              href={`/community/${post.id}`}
                              className="text-xs lg:text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors"
                            >
                              <span className="hidden sm:inline">
                                Read More →
                              </span>
                              <span className="sm:hidden">Read →</span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Load More */}
                  {currentPage < totalPages && (
                    <div className="text-center">
                      <button
                        onClick={() => fetchPosts(currentPage + 1, false)}
                        disabled={loading}
                        className="px-8 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <InlineLoader color="white" text="Loading..." />
                        ) : (
                          "Load More Posts"
                        )}
                      </button>
                    </div>
                  )}

                  {/* End of List Message */}
                  {!loading &&
                    currentPage >= totalPages &&
                    filteredAndSortedPosts.length > 0 && (
                      <div className="mt-12 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full mb-4">
                          <svg
                            className="w-8 h-8 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          You&apos;ve reached the end!
                        </h3>
                        <p className="text-gray-600 mb-4">
                          No more posts to load.{" "}
                          {searchQuery || selectedCategory
                            ? "Try adjusting your filters to see more results."
                            : "Check back soon for new community discussions!"}
                        </p>
                      </div>
                    )}

                  {/* Empty State */}
                  {!loading && filteredAndSortedPosts.length === 0 && (
                    <div className="text-center my-12 p-8 bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200">
                      <div className="text-6xl mb-4">
                        {searchQuery || selectedCategory ? "🔍" : "💬"}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">
                        {searchQuery || selectedCategory
                          ? "No posts found"
                          : "No posts yet"}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {searchQuery || selectedCategory
                          ? "We couldn't find any posts matching your criteria."
                          : "Be the first to start a conversation in the community!"}
                      </p>
                      {searchQuery || selectedCategory ? (
                        <button
                          onClick={() => {
                            setSearchQuery("");
                            setSelectedCategory("");
                          }}
                          className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                        >
                          Clear All Filters
                        </button>
                      ) : user ? (
                        <Link
                          href="/community/create"
                          className="inline-flex items-center gap-2 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                        >
                          <PlusIcon className="w-5 h-5" />
                          Create First Post
                        </Link>
                      ) : (
                        <Link
                          href="/login?redirect=/community/create"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-300"
                        >
                          <PlusIcon className="w-5 h-5" />
                          Login to Create Post
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }

        .animate-slideUp {
          animation: slideUp 0.5s ease-out forwards;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default CommunityPage;
